import { useEffect, useRef } from 'react';
import { captureMetrics, createKatalTimerStopWatch } from "src/katalMetrics/metricsHelperMethods";
import { ACTION_METHODS } from './metricsModels';

// Do not remove this
import * as KatalMetrics from '@amzn/katal-metrics';

const IDLE_TIMEOUT_MS = 2 * 60_000;        // 2 minutes
const MAX_COMPONENT_TIME_MS = 15 * 60_000; // 15 minutes
const MIN_COMPONENT_TIME_MS = 1000;        // 1 second

export function useVisibleTimeTracker(
  metricsConfig,
  timerConfigMetricName,
  actionMethod,
  siteService,
  dependencies = []
) {
  const timerRef = useRef(null);
  const activeRef = useRef(false);
  const idleTimeoutRef = useRef(null);

  useEffect(() => {
    const start = () => {
      if (activeRef.current) return;
      if (document.visibilityState !== 'visible') return;

      timerRef.current = createKatalTimerStopWatch(timerConfigMetricName);
      timerRef.current.start();
      activeRef.current = true;

      resetIdleTimeout();
    };

    const stop = () => {
      if (!activeRef.current) return;

      const timer = timerRef.current;
      if (timer) {
        timer.stop();

        let elapsed = timer.stopTime - timer.startTime;

        // Apply min/max caps
        if (elapsed >= MIN_COMPONENT_TIME_MS) {
          elapsed = Math.min(elapsed, MAX_COMPONENT_TIME_MS);

          captureMetrics(actionMethod, metricsConfig, siteService)
            .publishTimerMonitor(timerConfigMetricName, elapsed);
        }
      }

      timerRef.current = null;
      activeRef.current = false;

      clearIdleTimeout();
    };

    const resetIdleTimeout = () => {
      clearIdleTimeout();
      idleTimeoutRef.current = window.setTimeout(stop, IDLE_TIMEOUT_MS);
    };

    const clearIdleTimeout = () => {
      if (idleTimeoutRef.current !== null) {
        clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = null;
      }
    };

    const handleActivity = () => {
      if (activeRef.current) {
        resetIdleTimeout(); // extend active period
      } else if (document.visibilityState === 'visible') {
        start(); // restart timer after being idle
      }
    };

    const handleVisibilityChange = () => {
      document.visibilityState === 'visible' ? start() : stop();
    };

    const activityEvents = ['mousemove', 'keydown', 'scroll', 'pointerdown'];
    activityEvents.forEach(e =>
      window.addEventListener(e, handleActivity, { passive: true })
    );

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', stop);

    // Start timer immediately if visible
    start();

    return () => {
      stop();
      activityEvents.forEach(e =>
        window.removeEventListener(e, handleActivity)
      );
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', stop);
    };
  }, dependencies);
}

export default useVisibleTimeTracker;
